/**
 * Event type rules, shared by every place a person types or picks an event
 * type that gets saved: the pipeline wizard's Event Type and the holiday ID
 * (a holiday's pipeline uses the holiday ID as its event type).
 *
 * lambda-comms-event-listener finds an event's pipelines by an exact match on
 * `event_type`, so a pipeline saved as " Dussehra_2026" never answered the
 * holiday job's "Dussehra_2026" event and its comms never went out. An event
 * type is therefore trimmed before it is saved or compared, and may not hold
 * whitespace anywhere.
 *
 * Bulk Send and Scheduled sends pick an event type from the saved list and
 * send it exactly as saved, so they do not use these rules: trimming there
 * would stop a saved " name" pipeline from ever being reached.
 */

// Whitespace as JavaScript's \s knows it (space, tab, newlines, U+00A0
// no-break space, U+FEFF, the U+2000 range ...) plus the zero-width
// characters \s leaves out (U+200B to U+200D, U+2060). They arrive when a
// name is pasted from a document or a chat.
const SPACE = '[\\s\\u200B-\\u200D\\u2060]'
const EDGES = new RegExp(`^${SPACE}+|${SPACE}+$`, 'g')
const ANY_SPACE = new RegExp(SPACE)

/** The text with whitespace and zero-width characters removed from both ends. */
export const trimSpaces = (value) => String(value ?? '').replace(EDGES, '')

/** The event type with whitespace and zero-width characters removed from both ends. */
export const normalizeEventType = trimSpaces

/**
 * Why an event type cannot be saved, or '' when it can (or is still empty:
 * the step's required check covers that). Checks the trimmed value, so only
 * whitespace inside it is an error.
 *
 * @param {string} value
 * @param {object} [opts]
 * @param {string} [opts.label]    what the field is called in the message
 * @param {string} [opts.example]  a valid value to suggest
 */
export const eventTypeError = (
    value,
    { label = 'Event type', example = 'diwali_offer_2026' } = {}
) =>
    ANY_SPACE.test(normalizeEventType(value))
        ? `${label} can't contain spaces. Use underscores or hyphens, like ${example}.`
        : ''

/** True when the event type, trimmed, is not empty and has no whitespace inside. */
export const isValidEventType = (value) => {
    const v = normalizeEventType(value)
    return v !== '' && !ANY_SPACE.test(v)
}

/** Shown under an edited pipeline's event type when the saved one breaks the rules. */
export const SAVED_EVENT_TYPE_NOTE =
    'Saved with spaces in it, so only an event sent with exactly these spaces reaches this pipeline. It is kept as saved; to rename it, check what sends the event first.'

/**
 * The event type the wizard keeps for what was typed or picked.
 *
 * Editing: the saved event type, picked again or never touched, stays
 * exactly as saved. Trimming it would rename the trigger of a live pipeline
 * (the update replaces the row), and whatever sends the event with its
 * spaces (a Bulk Send picks the saved value) would no longer reach it.
 * Anything else is trimmed.
 *
 * @param {string} value  typed or picked
 * @param {string|null} [saved]  the pipeline's saved event type when editing
 */
export const pipelineEventType = (value, saved = null) =>
    saved !== null && saved !== undefined && value === saved
        ? saved
        : normalizeEventType(value)

/**
 * The pipeline name to save: trimmed like an event type (" Halloween_(USA)_usonly"
 * was saved with a leading space), except an edited pipeline's saved name left
 * as it is, which stays exactly as saved: its version history is looked up by
 * the name (/comms/pipeline/history/{pipeline_name}).
 *
 * @param {string} value
 * @param {string|null} [saved]  the pipeline's saved name when editing
 */
export const pipelineName = (value, saved = null) =>
    saved !== null && saved !== undefined && value === saved
        ? saved
        : trimSpaces(value)

/**
 * The wizard's check of its event type (already passed through
 * pipelineEventType).
 *
 * A new event type, or an edit that changes it, must be non-empty with no
 * whitespace inside: `error` says so under the field and `valid` holds Next.
 * An edited pipeline's saved event type, left as it is, is never an error,
 * so a pipeline saved with a space can still have its other fields edited;
 * `note` says why the space stays.
 *
 * @param {string} value
 * @param {string|null} [saved]
 * @returns {{ valid: boolean, error: string, note: string }}
 */
export const checkPipelineEventType = (value, saved = null) => {
    if (saved !== null && saved !== undefined && value === saved)
        return {
            valid: normalizeEventType(saved) !== '',
            error: '',
            // Spaces at the ends count too: they are what stops a match.
            note:
                saved === normalizeEventType(saved) && isValidEventType(saved)
                    ? ''
                    : SAVED_EVENT_TYPE_NOTE,
        }
    const error = eventTypeError(value)
    return {
        valid: normalizeEventType(value) !== '' && !error,
        error,
        note: '',
    }
}
